import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Chip,
  Divider,
  Alert,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ControlsBar from "./ControlsBar";
import { useLanguage } from "../contexts/LanguageContext";
import posts from "../blog/posts";
import SEOHead from "./SEOHead";

interface BlogPostProps {
  slug: string;
}

const BlogPost: React.FC<BlogPostProps> = ({ slug }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { isRTL } = useLanguage();
  const post = posts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <>
        <ControlsBar />
        <Box sx={{ minHeight: "100vh", backgroundColor: "background.default", py: 6 }}>
          <Container maxWidth="md">
            <Link href="#/blog" underline="hover" sx={{ color: "primary.main", fontWeight: 600 }}>
              {isRTL ? "→" : "←"} Back to Blog
</Link>
            <Typography variant="h5" sx={{ mt: 4 }}>Article not found.</Typography>
          </Container>
        </Box>
      </>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: {
      "@type": "Organization",
      name: "Resume Optimizer AI",
      url: "https://my-aitools.online/resume-optimizer/",
    },
    publisher: {
      "@type": "Organization",
      name: "Resume Optimizer AI",
      url: "https://my-aitools.online/resume-optimizer/",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://my-aitools.online/resume-optimizer/#/blog/${post.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${post.title} | Resume Optimizer AI`}
        description={post.excerpt}
        keywords={`${post.category.toLowerCase()}, resume tips UAE, ${post.title.toLowerCase()}, Dubai jobs`}
        canonicalUrl={`https://my-aitools.online/resume-optimizer/#/blog/${post.slug}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <ControlsBar />
      <Box sx={{ minHeight: "100vh", backgroundColor: "background.default", py: 6 }}>
        <Container maxWidth="md">
          {/* Back link */}
          <Link
            href="#/blog"
            underline="hover"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 4, color: "primary.main", fontWeight: 600, fontSize: "0.95rem" }}
          >
            {isRTL ? "→" : "←"} Back to Blog
          </Link>

          <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, backgroundColor: "background.paper" }}>
            {/* Meta */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip
                label={post.category}
                size="small"
                sx={{
                  backgroundColor: isDark ? "rgba(255,180,106,0.15)" : "rgba(255,132,40,0.1)",
                  color: "primary.main",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {post.date} · {post.readTime}
              </Typography>
            </Stack>

            {/* Title */}
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em", mb: 3 }}>
              {post.title}
            </Typography>

            <Divider sx={{ mb: 4 }} />

            {/* Content */}
            <Box>
              {post.content.map((section, i) => {
                if (section.type === "paragraph") {
                  return (
                    <Typography
                      key={i}
                      variant="body1"
                      color="text.secondary"
                      sx={{ lineHeight: 1.85, mb: 2.5 }}
                    >
                      {section.text}
                    </Typography>
                  );
                }
                if (section.type === "heading") {
                  return (
                    <Typography
                      key={i}
                      variant="h6"
                      sx={{ fontWeight: 700, mt: 4, mb: 1.5, color: "text.primary" }}
                    >
                      {section.text}
                    </Typography>
                  );
                }
                if (section.type === "list") {
                  return (
                    <Box
                      key={i}
                      component="ul"
                      sx={{ pl: 3, mb: 2.5, color: "text.secondary" }}
                    >
                      {section.items?.map((item, j) => (
                        <Typography
                          key={j}
                          component="li"
                          variant="body1"
                          sx={{ lineHeight: 1.85, mb: 0.5 }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  );
                }
                if (section.type === "tip") {
                  return (
                    <Alert
                      key={i}
                      severity="info"
                      sx={{
                        mt: 3,
                        mb: 2,
                        borderRadius: 2,
                        backgroundColor: isDark ? "rgba(255,180,106,0.1)" : "rgba(255,132,40,0.08)",
                        color: "text.primary",
                        "& .MuiAlert-icon": { color: "primary.main" },
                        border: "1px solid",
                        borderColor: isDark ? "rgba(255,180,106,0.2)" : "rgba(255,132,40,0.2)",
                      }}
                    >
                      {section.text}
                    </Alert>
                  );
                }
                return null;
              })}
            </Box>

            <Divider sx={{ mt: 5, mb: 3 }} />

            {/* CTA */}
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                Ready to optimize your resume?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Try our free AI resume optimizer — no sign-up required.
              </Typography>
              <Link
                href="#/"
                sx={{
                  display: "inline-block",
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  fontWeight: 700,
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  transition: "all 0.2s",
                  "&:hover": { opacity: 0.9, transform: "translateY(-1px)" },
                }}
              >
                Try Resume Optimizer →
              </Link>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default BlogPost;
